import { useEffect, useMemo, useRef, useState } from "react";
import { IconDeviceFloppy } from "@tabler/icons-react";
import Slider from "react-slick";
import { useFormik } from "formik";
import * as yup from "yup";
import InlineError from "@components/forms/InlineError";
import MediaPicker from "@components/media/MediaPicker";
import { CURRENCY_ISO_OPTIONS } from "@configs/currencies";
import { TOUR_STATUS_OPTIONS } from "@utils/tours/tourStatus";
import {
  isPersistedStartPoint,
  normalizeStartPointHtmlValue,
  normalizeStartPointLocalizedValue,
} from "@utils/start_point/panelUtils";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

function buildStartPointValidationSchema(activeIsos = []) {
  const requiredIsos = activeIsos.includes("en")
    ? ["en"]
    : activeIsos.length
      ? [activeIsos[0]]
      : [];
  const nameShape = activeIsos.reduce((acc, iso) => {
    acc[iso] = requiredIsos.includes(iso)
      ? yup.string().trim().required(`${iso.toUpperCase()} name is required.`)
      : yup.string().nullable().transform(() => "");
    return acc;
  }, {});
  const descriptionShape = activeIsos.reduce((acc, iso) => {
    acc[iso] = requiredIsos.includes(iso)
      ? yup
          .string()
          .trim()
          .required(`${iso.toUpperCase()} description is required.`)
      : yup.string().nullable().transform(() => "");
    return acc;
  }, {});

  return yup.object({
    status: yup.number().required("Status is required."),
    name: yup.object().shape(nameShape).required("Name is required."),
    description: yup
      .object()
      .shape(descriptionShape)
      .required("Description is required."),
    bannerId: yup.string().trim().required("Banner is required."),
    currencyIso: yup.string().trim().required("Currency is required."),
    amount: yup
      .number()
      .typeError("Amount is required.")
      .required("Amount is required.")
      .moreThan(0, "Amount must be greater than 0."),
    appleAppStoreId: yup
      .string()
      .trim()
      .required("Apple App Store ID is required."),
    googlePlayStoreId: yup
      .string()
      .trim()
      .required("Google Play Store ID is required."),
  });
}

function buildLocalizedDescription(point = {}, activeIsos = []) {
  return activeIsos.reduce((acc, iso) => {
    acc[iso] = normalizeStartPointHtmlValue(point?.description?.[iso]);
    return acc;
  }, {});
}

function buildLocalizedName(point = {}, activeIsos = []) {
  return activeIsos.reduce((acc, iso) => {
    acc[iso] = normalizeStartPointLocalizedValue(point?.name?.[iso]);
    return acc;
  }, {});
}

function normalizeAdditionalInfoImages(images) {
  return (Array.isArray(images) ? images : images ? [images] : [])
    .filter((image) => image && (image.id || image.url))
    .slice(0, 10);
}

const additionalInfoSliderSettings = {
  arrows: true,
  dots: false,
  infinite: true,
  speed: 260,
  slidesToShow: 1,
  slidesToScroll: 1,
  adaptiveHeight: false,
};

function AdditionalInfoImageSlider({ images = [] }) {
  const sliderRef = useRef(null);

  useEffect(() => {
    const resizeSlider = () => {
      sliderRef.current?.innerSlider?.onWindowResized?.();
    };
    const timeoutId = window.setTimeout(resizeSlider, 80);
    window.requestAnimationFrame(resizeSlider);
    return () => window.clearTimeout(timeoutId);
  }, [images]);

  return (
    <div className="start-point-tool-additional-info-slider">
      <Slider ref={sliderRef} {...additionalInfoSliderSettings}>
        {images.map((image, imageIndex) => (
          <div
            className="start-point-tool-additional-info-slide"
            key={image.id || image.url || imageIndex}
          >
            <div className="start-point-tool-additional-info-slide-frame">
              <img
                className="start-point-tool-additional-info-image"
                src={image.url}
                alt={`Additional information ${imageIndex + 1}`}
              />
              <span className="start-point-tool-additional-info-counter">
                {imageIndex + 1}/{images.length}
              </span>
            </div>
          </div>
        ))}
      </Slider>
    </div>
  );
}

function StartPointAddUpdate({
  point,
  languageList,
  activeLanguageIso,
  setStartPoints,
  uploadingBannerByPointId,
  bannerErrorByPointId,
  setBannerErrorByPointId,
  onOpenDescriptionEditor,
  onUploadPointBanner,
  onSaveStartPoint,
  savingPointId,
}) {
  const [lockSaveUntilValid, setLockSaveUntilValid] = useState(false);
  const activeIsos = useMemo(
    () => (languageList || []).map((language) => language.iso).filter(Boolean),
    [languageList],
  );
  const additionalInfoImages = useMemo(
    () => normalizeAdditionalInfoImages(point?.additionalInfo?.image),
    [point?.additionalInfo?.image],
  );
  const currentLanguageIso = useMemo(() => {
    if (activeIsos.includes(activeLanguageIso)) return activeLanguageIso;
    return activeIsos[0] || "";
  }, [activeIsos, activeLanguageIso]);
  const requiredLanguageIso = useMemo(() => {
    if (activeIsos.includes("en")) return "en";
    return activeIsos[0] || "";
  }, [activeIsos]);
  const currentLanguageLabel = useMemo(() => {
    const currentLanguage = (languageList || []).find(
      (language) => language.iso === currentLanguageIso,
    );
    return currentLanguage?.name || currentLanguageIso.toUpperCase();
  }, [currentLanguageIso, languageList]);

  const formik = useFormik({
    enableReinitialize: true,
    validateOnMount: true,
    initialValues: {
      status: Number(point?.status) || 1,
      name: buildLocalizedName(point, activeIsos),
      description: buildLocalizedDescription(point, activeIsos),
      bannerId: point?.banner?.id || "",
      currencyIso: point?.price?.currencyIso || "USD",
      amount: point?.price?.amount ?? 0,
      appleAppStoreId: String(point?.appleAppStoreId || ""),
      googlePlayStoreId: String(point?.googlePlayStoreId || ""),
    },
    validationSchema: buildStartPointValidationSchema(activeIsos),
    onSubmit: () => {},
  });

  useEffect(() => {
    if (lockSaveUntilValid && formik.isValid) {
      setLockSaveUntilValid(false);
    }
  }, [formik.isValid, lockSaveUntilValid]);

  function touchAllRequiredFields() {
    const touchedByIso = {
      description: requiredLanguageIso ? { [requiredLanguageIso]: true } : {},
      name: requiredLanguageIso ? { [requiredLanguageIso]: true } : {},
    };

    formik.setTouched({
      status: true,
      bannerId: true,
      currencyIso: true,
      amount: true,
      appleAppStoreId: true,
      googlePlayStoreId: true,
      ...touchedByIso,
    });
  }

  async function handleSaveClick() {
    const errors = await formik.validateForm();
    if (Object.keys(errors || {}).length) {
      touchAllRequiredFields();
      setLockSaveUntilValid(true);
      return;
    }
    onSaveStartPoint(point);
  }

  function pickLocalizedFieldError(fieldName) {
    if (currentLanguageIso !== requiredLanguageIso) return "";
    const touchedField = formik.touched?.[fieldName];
    const fieldErrors = formik.errors?.[fieldName];
    if (!touchedField || typeof touchedField !== "object") return "";
    return currentLanguageIso
      ? String(fieldErrors?.[currentLanguageIso] || "")
      : "";
  }

  return (
    <div className="start-point-tool-form">
      <label className="field-label app-form-field">
        <span className="label-inline">
          Status
          {formik.touched.status && formik.errors.status ? (
            <InlineError>{formik.errors.status}</InlineError>
          ) : null}
        </span>
        <select
          className="field-input select-control"
          value={point.status}
          onChange={(event) => {
            const nextValue = Number(event.target.value);
            setStartPoints?.((currentPoints) =>
              (Array.isArray(currentPoints) ? currentPoints : []).map((item) =>
                item.id === point.id ? { ...item, status: nextValue } : item,
              ),
            );
            formik.setFieldValue("status", nextValue, true);
            formik.setFieldTouched("status", true, true);
          }}
        >
          {TOUR_STATUS_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.value} - {option.label}
            </option>
          ))}
        </select>
      </label>
      <div>
        <p className="tour-add-form-label">
          name
          {pickLocalizedFieldError("name") ? (
            <InlineError>{pickLocalizedFieldError("name")}</InlineError>
          ) : null}
        </p>
        <label className="field-label app-form-field">
          <input
            className="field-input"
            value={normalizeStartPointLocalizedValue(
              point?.name?.[currentLanguageIso],
            )}
            placeholder={`Write name (${currentLanguageIso})`}
            onChange={(event) => {
              const nextValue = event.target.value;
              setStartPoints?.((currentPoints) =>
                (Array.isArray(currentPoints) ? currentPoints : []).map((item) =>
                  item.id === point.id
                    ? {
                        ...item,
                        name: {
                          ...(item?.name || {}),
                          [currentLanguageIso]: nextValue,
                        },
                      }
                    : item,
                ),
              );
              formik.setFieldValue(
                `name.${currentLanguageIso}`,
                nextValue,
                true,
              );
            }}
            onBlur={() =>
              formik.setFieldTouched(`name.${currentLanguageIso}`, true, true)
            }
          />
        </label>
      </div>
      <div>
        <p className="tour-add-form-label">
          description
          {pickLocalizedFieldError("description") ? (
            <InlineError>{pickLocalizedFieldError("description")}</InlineError>
          ) : null}
        </p>
        <button
          type="button"
          className={`field-label app-form-field field-preview-row ${point?.description?.[currentLanguageIso] ? "filled" : ""}`}
          onClick={() => {
            formik.setFieldTouched(
              `description.${currentLanguageIso}`,
              true,
              true,
            );
            onOpenDescriptionEditor(point.id, currentLanguageIso);
          }}
        >
          {normalizeStartPointHtmlValue(
            point?.description?.[currentLanguageIso],
          ) ? (
            <span
              className="field-preview-value field-preview-value-html"
              dangerouslySetInnerHTML={{
                __html: normalizeStartPointHtmlValue(
                  point?.description?.[currentLanguageIso],
                ),
              }}
            />
          ) : (
            <span className="field-preview-value">Complete</span>
          )}
        </button>
      </div>
      <MediaPicker
        title="banner (required)"
        titleError={
          formik.touched.bannerId && formik.errors.bannerId
            ? formik.errors.bannerId
            : ""
        }
        items={point?.banner?.id ? [point.banner] : []}
        isUploading={Boolean(uploadingBannerByPointId[point.id])}
        uploadError={bannerErrorByPointId[point.id] || ""}
        accept={{ "image/*": [] }}
        maxFiles={1}
        invalidTypeMessage="Banner must be an image."
        onRejected={(message) =>
          setBannerErrorByPointId((prev) => ({ ...prev, [point.id]: message }))
        }
        onUpload={(files) => {
          formik.setFieldTouched("bannerId", true, true);
          onUploadPointBanner(point.id, files);
        }}
        onRemove={() => {
          formik.setFieldTouched("bannerId", true, true);
          setStartPoints?.((currentPoints) =>
            (Array.isArray(currentPoints) ? currentPoints : []).map((item) =>
              item.id === point.id ? { ...item, banner: null } : item,
            ),
          );
        }}
        uploadLabel="Upload banner image"
        uploadHint="Image only for this driving tour points."
      />
      <div className="start-point-tool-store-grid">
        <div>
          <p className="tour-add-form-label">
            Apple App Store ID
            {formik.touched.appleAppStoreId && formik.errors.appleAppStoreId ? (
              <InlineError>{formik.errors.appleAppStoreId}</InlineError>
            ) : null}
          </p>
          <label className="field-label app-form-field">
            <input
              className="field-input"
              value={String(point?.appleAppStoreId || "")}
              onChange={(event) => {
                const nextValue = event.target.value;
                setStartPoints?.((currentPoints) =>
                  (Array.isArray(currentPoints) ? currentPoints : []).map((item) =>
                    item.id === point.id
                      ? {
                          ...item,
                          appleAppStoreId: nextValue,
                        }
                      : item,
                  ),
                );
                formik.setFieldValue("appleAppStoreId", nextValue, false);
              }}
              onBlur={() =>
                formik.setFieldTouched("appleAppStoreId", true, true)
              }
            />
          </label>
        </div>
        <div>
          <p className="tour-add-form-label">
            Google Play Store ID
            {formik.touched.googlePlayStoreId &&
            formik.errors.googlePlayStoreId ? (
              <InlineError>{formik.errors.googlePlayStoreId}</InlineError>
            ) : null}
          </p>
          <label className="field-label app-form-field">
            <input
              className="field-input"
              value={String(point?.googlePlayStoreId || "")}
              onChange={(event) => {
                const nextValue = event.target.value;
                setStartPoints?.((currentPoints) =>
                  (Array.isArray(currentPoints) ? currentPoints : []).map((item) =>
                    item.id === point.id
                      ? {
                          ...item,
                          googlePlayStoreId: nextValue,
                        }
                      : item,
                  ),
                );
                formik.setFieldValue("googlePlayStoreId", nextValue, false);
              }}
              onBlur={() =>
                formik.setFieldTouched("googlePlayStoreId", true, true)
              }
            />
          </label>
        </div>
      </div>
      <div className="start-point-tool-price-card">
        <div className="start-point-tool-price-note">
          <p className="tour-add-form-label">website base price</p>
          <p className="start-point-tool-help start-point-tool-price-note-text">
            This is a base price for web site only, the tour prices in iOS and
            Android are managed by the Apple Appstore and Google Playstore.
          </p>
        </div>
        <div className="start-point-tool-price-grid">
          <label className="field-label app-form-field">
            <span className="label-inline">
              Currency
              {formik.touched.currencyIso && formik.errors.currencyIso ? (
                <InlineError>{formik.errors.currencyIso}</InlineError>
              ) : null}
            </span>
            <select
              className="field-input select-control"
              value={point?.price?.currencyIso || "USD"}
              onChange={(event) => {
                const nextValue = event.target.value;
                setStartPoints?.((currentPoints) =>
                  (Array.isArray(currentPoints) ? currentPoints : []).map((item) =>
                    item.id === point.id
                      ? {
                          ...item,
                          price: {
                            currencyIso: nextValue,
                            amount: item?.price?.amount ?? 0,
                          },
                        }
                      : item,
                  ),
                );
                formik.setFieldValue("currencyIso", nextValue, true);
                formik.setFieldTouched("currencyIso", true, true);
              }}
            >
              {CURRENCY_ISO_OPTIONS.map((iso) => (
                <option key={iso} value={iso}>
                  {iso}
                </option>
              ))}
            </select>
          </label>
          <label className="field-label app-form-field">
            <span className="label-inline">
              Amount
              {formik.touched.amount && formik.errors.amount ? (
                <InlineError>{formik.errors.amount}</InlineError>
              ) : null}
            </span>
            <input
              type="number"
              min="0.01"
              step="0.01"
              className="field-input"
              value={point?.price?.amount ?? 0}
              onChange={(event) => {
                const nextValue = event.target.value;
                setStartPoints?.((currentPoints) =>
                  (Array.isArray(currentPoints) ? currentPoints : []).map((item) =>
                    item.id === point.id
                      ? {
                          ...item,
                          price: {
                            currencyIso: item?.price?.currencyIso || "USD",
                            amount: nextValue,
                          },
                        }
                      : item,
                  ),
                );
                formik.setFieldValue("amount", nextValue, true);
              }}
              onBlur={() => formik.setFieldTouched("amount", true, true)}
            />
          </label>
        </div>
      </div>
      <div className="start-point-tool-additional-info-preview">
        <p className="tour-add-form-label">additional description information</p>
        <div className="start-point-tool-additional-info-preview-grid">
          <div>
            <span className="start-point-tool-additional-info-label">name</span>
            <p className="start-point-tool-additional-info-text">
              {normalizeStartPointLocalizedValue(
                point?.additionalInfo?.name?.[currentLanguageIso],
              ) || "-"}
            </p>
          </div>
          <div>
            <span className="start-point-tool-additional-info-label">
              description
            </span>
            {normalizeStartPointHtmlValue(
              point?.additionalInfo?.description?.[currentLanguageIso],
            ) ? (
              <div
                className="field-preview-value field-preview-value-html start-point-tool-additional-info-html"
                dangerouslySetInnerHTML={{
                  __html: normalizeStartPointHtmlValue(
                    point?.additionalInfo?.description?.[currentLanguageIso],
                  ),
                }}
              />
            ) : (
              <p className="start-point-tool-additional-info-text">-</p>
            )}
          </div>
          <div>
            <span className="start-point-tool-additional-info-label">images</span>
            {additionalInfoImages.length > 1 ? (
              <AdditionalInfoImageSlider images={additionalInfoImages} />
            ) : additionalInfoImages.length ? (
              <div className="start-point-tool-additional-info-image-grid">
                {additionalInfoImages.map((image, imageIndex) => (
                  <img
                    key={image.id || image.url || imageIndex}
                    className="start-point-tool-additional-info-image"
                    src={image.url}
                    alt={`Additional information ${imageIndex + 1}`}
                  />
                ))}
              </div>
            ) : (
              <p className="start-point-tool-additional-info-text">-</p>
            )}
          </div>
        </div>
      </div>
      <div className="start-point-tool-save-row">
        <button
          type="button"
          className={`app-icon-btn app-icon-btn--save ${savingPointId === point.id || (lockSaveUntilValid && !formik.isValid) ? "is-disabled" : ""} ${savingPointId === point.id ? "is-loading" : ""}`}
          onClick={handleSaveClick}
          disabled={
            savingPointId === point.id ||
            (lockSaveUntilValid && !formik.isValid)
          }
          aria-disabled={
            savingPointId === point.id ||
            (lockSaveUntilValid && !formik.isValid)
          }
          aria-label={
            isPersistedStartPoint(point)
              ? "Update  driving tour points"
              : "Save driving tour points"
          }
          title={
            isPersistedStartPoint(point)
              ? "Update  driving tour points"
              : "Save driving tour points"
          }
        >
          {savingPointId === point.id ? (
            <span className="app-icon-btn-spinner" />
          ) : (
            <IconDeviceFloppy size={20} />
          )}
        </button>
      </div>
    </div>
  );
}

export default StartPointAddUpdate;
